<template>
    <div v-if="shouldShowLauncher" class="floating-ai-chat-launcher fixed right-4 z-[10000] md:right-5"
        :style="wrapperStyle">
        <transition mode="out-in" enter-active-class="transition duration-200 ease-out"
            enter-class="translate-y-3 opacity-0" enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in" leave-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-3 opacity-0">
            <section v-if="showPanel" key="panel"
                class="floating-ai-chat-panel mb-3 flex h-[min(88vh,920px)] flex-col overflow-hidden rounded-[30px] border border-base-300 bg-gradient-to-b from-base-100 via-base-100 to-base-200/40 shadow-2xl"
                :class="{ 'is-wide': isPanelWide }">
                <div class="relative flex min-h-0 flex-1 overflow-hidden">
                    <transition enter-active-class="transition duration-200 ease-out"
                        enter-class="-translate-x-4 opacity-0" enter-to-class="translate-x-0 opacity-100"
                        leave-active-class="transition duration-150 ease-in" leave-class="translate-x-0 opacity-100"
                        leave-to-class="-translate-x-4 opacity-0">
                        <aside v-if="showSessions || isPanelWide"
                            class="floating-ai-chat-sidebar absolute inset-y-0 left-0 z-20 border-r border-base-300 bg-base-100/95 shadow-xl backdrop-blur"
                            :class="{ 'is-wide': isPanelWide, 'is-session-drawer': showSessions }">
                            <div class="flex h-full flex-col gap-3 p-3 md:p-4">
                                <div class="flex items-start justify-between gap-3">
                                    <div class="min-w-0">
                                        <div class="text-[11px] font-semibold text-primary">Music Tag Web</div>
                                        <div class="mt-1 truncate text-base font-semibold text-base-content">AI 助手工作台</div>
                                        <div class="mt-1 text-xs leading-5 text-base-content/55">管理对话、查看执行流，并让助手处理项目内任务。</div>
                                    </div>
                                    <button type="button" class="btn btn-ghost btn-xs btn-circle"
                                        v-if="!isPanelWide || showSessions"
                                        @click="showSessions = false">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M6 6l12 12M18 6L6 18" />
                                        </svg>
                                    </button>
                                </div>

                                <div class="grid grid-cols-2 gap-2 rounded-[22px] border border-base-300 bg-base-200/40 p-2 text-center">
                                    <div class="rounded-2xl bg-base-100 px-2 py-2 shadow-sm">
                                        <div class="text-base font-semibold text-base-content">{{ chat.sessionList.length }}</div>
                                        <div class="text-[11px] text-base-content/50">会话</div>
                                    </div>
                                    <div class="rounded-2xl bg-base-100 px-2 py-2 shadow-sm">
                                        <div class="text-base font-semibold text-base-content">{{ chat.messages.length }}</div>
                                        <div class="text-[11px] text-base-content/50">消息</div>
                                    </div>
                                </div>

                                <button type="button" class="btn btn-primary btn-sm w-full rounded-2xl"
                                    :disabled="chat.isCurrentSessionLoading" @click="startNewDraft">
                                    新建对话
                                </button>

                                <div class="min-h-0 flex-1 overflow-y-auto pr-1">
                                    <div class="mb-2 text-xs font-medium text-base-content/55">最近会话</div>
                                    <button v-for="session in chat.sessionList" :key="session.id" type="button"
                                        class="mb-2 block w-full rounded-2xl border px-3 py-3 text-left transition duration-200"
                                        :class="chat.activeSessionId === session.id ? 'border-primary bg-primary text-primary-content shadow-lg shadow-primary/15' : 'border-base-300 bg-base-100/90 text-base-content shadow-sm hover:-translate-y-0.5 hover:bg-base-100 hover:shadow-md'"
                                        @click="selectSession(session.id)">
                                        <div class="line-clamp-2 text-xs font-medium leading-5">
                                            {{ chat.getSessionTitle(session) }}
                                        </div>
                                        <div
                                            class="mt-2 flex items-center justify-between gap-2 text-[11px] opacity-70">
                                            <span class="truncate">{{ shortSessionId(session.id) }}</span>
                                            <button type="button" class="link link-hover"
                                                @click.stop="deleteSession(session.id)">
                                                删除
                                            </button>
                                        </div>
                                    </button>

                                    <div v-if="!chat.sessionList.length"
                                        class="rounded-2xl border border-dashed border-base-300 px-3 py-8 text-center text-xs text-base-content/60">
                                        先发一条消息，这里会保存你的会话。
                                    </div>
                                </div>

                                <div v-if="isPanelWide" class="floating-ai-chat-sidebar-prompts rounded-[22px] border border-base-300 bg-base-100/80 p-3 shadow-sm">
                                    <div class="mb-2 text-xs font-medium text-base-content/60">快捷问题</div>
                                    <button v-for="prompt in quickPrompts.slice(0, 3)" :key="'side-' + prompt" type="button"
                                        class="mb-2 block w-full rounded-2xl bg-base-200/60 px-3 py-2.5 text-left text-xs leading-5 text-base-content/70 transition hover:bg-base-200"
                                        @click="applyQuickPrompt(prompt)">
                                        {{ prompt }}
                                    </button>
                                </div>
                            </div>
                        </aside>
                    </transition>

                    <button v-if="showSessions" type="button" class="floating-ai-chat-session-backdrop absolute inset-0 z-10 bg-base-content/5"
                        @click="showSessions = false">
                    </button>

                    <div class="floating-ai-chat-main flex min-w-0 flex-1 flex-col bg-base-100">
                        <div
                            class="floating-ai-chat-header flex items-center justify-between gap-3 border-b border-base-300 bg-base-100/70 px-4 py-2.5 text-xs text-base-content/60 backdrop-blur md:px-5">
                            <div class="flex min-w-0 items-center gap-3">
                                <button type="button" class="floating-ai-chat-session-toggle btn btn-ghost btn-xs btn-circle"
                                    @click="showSessions = true">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M4 7h16M4 12h12M4 17h9" />
                                    </svg>
                                </button>
                                <div class="min-w-0">
                                    <div class="truncate text-sm font-semibold text-base-content">{{ activeSessionLabel }}</div>
                                    <div v-if="isPanelWide" class="floating-ai-chat-header-hint truncate text-[11px] text-base-content/45">
                                        右侧保持输入，左侧管理上下文
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <span v-if="chat.isCurrentSessionLoading"
                                    class="badge badge-warning badge-sm shrink-0 whitespace-nowrap">生成中</span>
                                <button type="button" class="btn btn-ghost btn-xs" :disabled="!chat.activeSessionId"
                                    @click="reloadMessages">
                                    刷新
                                </button>
                                <button type="button" class="btn btn-ghost btn-xs btn-circle" :title="panelSizeTitle"
                                    @click="togglePanelWide">
                                    <svg v-if="!isPanelWide" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                    </svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M9 3v6H3M15 21v-6h6M3 9l7-7M21 15l-7 7" />
                                    </svg>
                                </button>
                                <button type="button" class="btn btn-ghost btn-xs btn-circle" @click="closePanel">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div ref="messageScroller"
                            @scroll="handleMessageScroll"
                            class="floating-ai-chat-scroller min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-base-200/10 via-base-200/30 to-base-100 px-4 py-5 md:px-5">
                            <div class="floating-ai-chat-thread mx-auto space-y-4">
                                <article v-for="(message, index) in chat.messages" :key="message.messageId || index"
                                    class="flex" :class="message.role === 'user' ? 'justify-end' : 'justify-start'">
                                    <div class="floating-ai-chat-message max-w-[90%] md:max-w-[84%]">
                                        <div class="mb-1.5 px-1 text-[11px] text-base-content/45"
                                            :class="message.role === 'user' ? 'text-right' : 'text-left'">
                                            {{ message.role === 'user' ? '你' : '助手' }}
                                        </div>
                                        <div class="min-w-0 overflow-hidden px-4 py-3.5 text-sm leading-7 transition"
                                            :class="message.role === 'user' ? 'rounded-[24px] rounded-br-lg bg-primary text-primary-content shadow-lg shadow-primary/15' : 'rounded-[24px] rounded-bl-lg border border-base-300 bg-base-100/95 text-base-content shadow-md'">
                                            <div v-if="message.reasoning"
                                                class="mb-3 overflow-hidden rounded-2xl border border-base-300/80 bg-base-200/80 text-xs leading-6 text-base-content/70"
                                                :class="isReasoningStreaming(message, index) ? 'ring-1 ring-primary/20' : ''">
                                                <button type="button"
                                                    class="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-base-100/40"
                                                    @click="toggleReasoning(message, index)">
                                                    <span
                                                        class="inline-flex items-center gap-2 rounded-full bg-base-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-base-content/60">
                                                        <span>思考过程</span>
                                                        <span v-if="isReasoningStreaming(message, index)"
                                                            class="inline-flex items-center gap-1 text-primary">
                                                            <span
                                                                class="h-1.5 w-1.5 animate-pulse rounded-full bg-current"></span>
                                                            <span class="loading loading-dots loading-xs"></span>
                                                        </span>
                                                    </span>
                                                    <svg xmlns="http://www.w3.org/2000/svg"
                                                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                                                        :class="isReasoningExpanded(message, index) ? 'rotate-180' : ''"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            d="M6 9l6 6 6-6" />
                                                    </svg>
                                                </button>
                                                <div v-if="isReasoningExpanded(message, index)"
                                                    class="border-t border-base-300/70 px-3 py-2.5"
                                                    v-html="chat.renderMarkdownHtml(message.reasoning)"></div>
                                            </div>
                                            <div v-if="hasVisibleProcessParts(message)" class="mb-3 space-y-2 text-xs leading-6 text-base-content">
                                                <template v-for="(part, partIndex) in getVisibleProcessParts(message)">
                                                <div :key="getProcessPartKey(message, index, part, partIndex)" class="overflow-hidden rounded-2xl border border-base-300/80 bg-base-100">
                                                    <button type="button" class="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-base-200/40" @click="toggleProcessPart(message, index, part, partIndex)">
                                                        <span class="min-w-0">
                                                            <span class="block truncate text-[11px] font-semibold tracking-wide text-base-content/75">
                                                                {{ getProcessPartLabel(part) }}
                                                            </span>
                                                            <span class="mt-0.5 block truncate text-[11px] text-base-content/55">
                                                                {{ getProcessPartPreview(part) }}
                                                            </span>
                                                        </span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0 transition-transform duration-200 text-base-content/45" :class="isProcessPartExpanded(message, index, part, partIndex) ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    </button>
                                                    <div v-if="isProcessPartExpanded(message, index, part, partIndex)" class="border-t border-base-300/70 px-3 py-2.5">
                                                        <pre class="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-6 text-base-content/80">{{ getProcessPartSummary(part, false) }}</pre>
                                                    </div>
                                                </div>
                                                </template>
                                            </div>
                                            <div v-if="message.role === 'assistant'">
                                                <div v-if="getPlaybackCommand(message)" class="ai-playback-card">
                                                    <div class="ai-playback-card__cover">
                                                        <img :src="getPlaybackCover(message)"
                                                            :alt="getPlaybackTitle(message)"
                                                            class="ai-playback-card__cover-image">
                                                        <span v-if="isPlaybackCardPlaying(message)"
                                                            class="ai-playback-card__equalizer" aria-hidden="true">
                                                            <i></i><i></i><i></i>
                                                        </span>
                                                    </div>
                                                    <div class="ai-playback-card__body">
                                                        <div class="ai-playback-card__eyebrow">
                                                            {{ getPlaybackActionLabel(message) }}
                                                        </div>
                                                        <div class="ai-playback-card__title">
                                                            {{ getPlaybackTitle(message) }}
                                                        </div>
                                                        <div class="ai-playback-card__artist">
                                                            {{ getPlaybackArtist(message) }}
                                                        </div>
                                                    </div>
                                                    <div class="ai-playback-card__actions">
                                                        <button type="button" class="ai-playback-card__button"
                                                            :title="isPlaybackCardPlaying(message) ? '暂停' : '播放'"
                                                            @click="handlePlaybackCardAction(message)">
                                                            <svg v-if="!isPlaybackCardPlaying(message)" viewBox="0 0 24 24"
                                                                aria-hidden="true">
                                                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z" />
                                                            </svg>
                                                            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                                                                <path d="M7 5h3v14H7zm7 0h3v14h-3z" />
                                                            </svg>
                                                        </button>
                                                        <button type="button" class="ai-playback-card__button ai-playback-card__button--ghost"
                                                            title="下一首" @click="handlePlaybackNext">
                                                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                                                <path d="M6 6.7v10.6a1 1 0 0 0 1.53.85L16 12.85a1 1 0 0 0 0-1.7L7.53 5.85A1 1 0 0 0 6 6.7zm11-.2h2v11h-2z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div v-else-if="hasDisplayParts(message)" class="space-y-3">
                                                    <template v-for="(displayPart, displayPartIndex) in getDisplayParts(message)">
                                                        <div v-if="displayPart.type === 'text'" :key="getDisplayPartKey(message, index, displayPart, displayPartIndex)" class="prose prose-sm max-w-none break-words text-inherit" v-html="chat.renderMarkdownHtml(displayPart.text)">
                                                        </div>
                                                        <div v-else-if="isQuestionToolPart(displayPart.part)" :key="getDisplayPartKey(message, index, displayPart, displayPartIndex)">
                                                            <form class="overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 text-xs leading-6 text-base-content" @submit.prevent="submitQuestionTool(message, index, displayPart.part, displayPartIndex)">
                                                                <div class="border-b border-primary/10 px-3 py-2.5">
                                                                    <div class="flex items-center justify-between gap-3">
                                                                        <div class="min-w-0">
                                                                            <div class="truncate text-[11px] font-semibold tracking-wide text-primary">需要确认</div>
                                                                            <div class="mt-0.5 truncate text-[11px] text-base-content/55">{{ getQuestionToolStatusText(displayPart.part) }}</div>
                                                                        </div>
                                                                        <span v-if="isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" class="badge badge-success badge-sm shrink-0 text-white">已提交</span>
                                                                    </div>
                                                                </div>
                                                                <div class="border-b border-primary/10 px-3 pt-3">
                                                                    <div class="flex max-w-full flex-nowrap gap-1 overflow-x-auto whitespace-nowrap rounded-2xl bg-base-100/70 p-1">
                                                                        <button v-for="(questionItem, questionIndex) in getQuestionToolQuestions(displayPart.part)" :key="questionIndex" type="button" class="btn btn-xs min-h-0 h-7 max-w-[9rem] shrink-0 rounded-full border-0 px-3 text-[11px] font-medium" :class="getQuestionActiveStep(message, index, displayPart.part, displayPartIndex) === questionIndex ? 'btn-primary' : 'btn-ghost bg-transparent'" :disabled="!canOpenQuestionStep(message, index, displayPart.part, displayPartIndex, questionIndex) || isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @click="setQuestionActiveStep(message, index, displayPart.part, displayPartIndex, questionIndex)">
                                                                            <span class="block min-w-0 truncate">{{ questionItem.header || ('问题 ' + (questionIndex + 1)) }}</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div class="space-y-3 px-3 py-3">
                                                                    <fieldset v-if="getActiveQuestionItem(message, index, displayPart.part, displayPartIndex)" class="min-w-0">
                                                                        <legend class="mb-2">
                                                                            <span class="block text-[11px] font-semibold text-base-content/70">{{ getActiveQuestionItem(message, index, displayPart.part, displayPartIndex).header || ('问题 ' + (getQuestionActiveStep(message, index, displayPart.part, displayPartIndex) + 1)) }}</span>
                                                                            <span class="mt-1 block text-sm font-medium leading-6 text-base-content">{{ getActiveQuestionItem(message, index, displayPart.part, displayPartIndex).question }}</span>
                                                                        </legend>
                                                                        <label v-for="(option, optionIndex) in getQuestionOptions(getActiveQuestionItem(message, index, displayPart.part, displayPartIndex))" :key="optionIndex" class="mb-2 flex cursor-pointer gap-2 rounded-2xl border border-base-300 bg-base-100 px-3 py-2.5 transition hover:border-primary/40 hover:bg-base-100/90" :class="isQuestionOptionSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex), option) ? 'border-primary bg-primary/10 ring-1 ring-primary/20' : ''">
                                                                            <input class="radio radio-primary radio-xs mt-1 shrink-0" type="radio" :name="getQuestionToolKey(message, index, displayPart.part, displayPartIndex) + ':' + getQuestionActiveStep(message, index, displayPart.part, displayPartIndex)" :value="getQuestionOptionValue(option)" :checked="isQuestionOptionSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex), option)" :disabled="isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @change="setQuestionToolAnswer(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex), option)">
                                                                            <span class="min-w-0">
                                                                                <span class="block break-words text-xs font-medium leading-5 text-base-content">{{ option.label }}</span>
                                                                                <span v-if="option.description" class="mt-0.5 block break-words text-[11px] leading-5 text-base-content/60">{{ option.description }}</span>
                                                                            </span>
                                                                        </label>
                                                                        <label class="mb-2 flex cursor-pointer gap-2 rounded-2xl border border-base-300 bg-base-100 px-3 py-2.5 transition hover:border-primary/40 hover:bg-base-100/90" :class="isQuestionCustomSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex)) ? 'border-primary bg-primary/10 ring-1 ring-primary/20' : ''">
                                                                            <input class="radio radio-primary radio-xs mt-1 shrink-0" type="radio" :name="getQuestionToolKey(message, index, displayPart.part, displayPartIndex) + ':' + getQuestionActiveStep(message, index, displayPart.part, displayPartIndex)" :value="customQuestionOptionValue" :checked="isQuestionCustomSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex))" :disabled="isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @change="setQuestionCustomSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex))">
                                                                            <span class="min-w-0 flex-1">
                                                                                <span class="block break-words text-xs font-medium leading-5 text-base-content">自定义答案</span>
                                                                                <input v-if="isQuestionCustomSelected(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex))" class="input input-bordered input-xs mt-2 w-full rounded-xl bg-base-100 text-xs" type="text" :value="getQuestionCustomAnswer(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex))" :disabled="isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" placeholder="请输入自定义答案" @input="setQuestionCustomAnswer(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex), $event.target.value)">
                                                                            </span>
                                                                        </label>
                                                                    </fieldset>
                                                                </div>
                                                                <div class="flex items-center justify-between gap-2 border-t border-primary/10 px-3 py-2.5">
                                                                    <button type="button" class="btn btn-ghost btn-xs rounded-full" :disabled="isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @click="rejectQuestionTool(message, index, displayPart.part, displayPartIndex)">
                                                                        拒绝
                                                                    </button>
                                                                    <div class="flex items-center gap-2">
                                                                        <button type="button" class="btn btn-ghost btn-xs rounded-full" :disabled="getQuestionActiveStep(message, index, displayPart.part, displayPartIndex) <= 0 || isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @click="goPrevQuestionStep(message, index, displayPart.part, displayPartIndex)">
                                                                            上一步
                                                                        </button>
                                                                        <button v-if="!isLastQuestionStep(message, index, displayPart.part, displayPartIndex)" type="button" class="btn btn-primary btn-xs rounded-full" :disabled="!canLeaveQuestionStep(message, index, displayPart.part, displayPartIndex, getQuestionActiveStep(message, index, displayPart.part, displayPartIndex)) || isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)" @click="goNextQuestionStep(message, index, displayPart.part, displayPartIndex)">
                                                                            下一步
                                                                        </button>
                                                                        <button v-else type="submit" class="btn btn-primary btn-xs rounded-full" :disabled="!canSubmitQuestionTool(message, index, displayPart.part, displayPartIndex) || isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) || isQuestionToolSubmitted(message, index, displayPart.part, displayPartIndex)">
                                                                            {{ isQuestionToolBusy(message, index, displayPart.part, displayPartIndex) ? '提交中' : '提交' }}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </template>
                                                </div>
                                                <div v-else class="prose prose-sm max-w-none break-words text-inherit"
                                                    v-html="chat.renderMarkdownHtml(message.content)">
                                                </div>
                                                <div v-if="getPlaybackStatus(message)"
                                                    class="mt-3 rounded-2xl border border-success/20 bg-success/10 px-3 py-2 text-xs leading-5 text-success">
                                                    {{ getPlaybackStatus(message) }}
                                                </div>
                                            </div>
                                            <div v-else class="whitespace-pre-wrap break-words">
                                                {{ message.content }}
                                            </div>
                                        </div>
                                    </div>
                                </article>

                                <div v-if="chat.isCurrentSessionLoading && !chat.hasCurrentSessionReplyContent"
                                    class="flex justify-start">
                                    <div
                                        class="rounded-[22px] rounded-bl-lg border border-base-300 bg-base-100/95 px-4 py-3 text-sm text-base-content shadow-md">
                                        <span class="loading loading-dots loading-sm"></span>
                                    </div>
                                </div>

                                <div v-if="!chat.messages.length"
                                    class="rounded-[28px] border border-dashed border-base-300 bg-gradient-to-b from-base-100 to-base-200/40 px-6 py-10 text-center text-sm text-base-content/60 shadow-md">
                                    <div
                                        class="mx-auto flex h-14 w-14 items-center justify-center rounded-[18px] bg-primary text-primary-content shadow-lg shadow-primary/15 ring-1 ring-primary/10">
                                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                                            class="launcher-icon h-8 w-8 shrink-0" fill="currentColor">
                                            <path
                                                d="M12 2.75L13.817 8.183L19.25 10L13.817 11.817L12 17.25L10.183 11.817L4.75 10L10.183 8.183L12 2.75Z"
                                                stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                            <path
                                                d="M18.5 15.5L19.232 17.518L21.25 18.25L19.232 18.982L18.5 21L17.768 18.982L15.75 18.25L17.768 17.518L18.5 15.5Z"
                                                fill="currentColor" />
                                        </svg>
                                    </div>
                                    <div class="mt-4 font-medium text-base-content">欢迎使用智能助手</div>
                                    <div class="mt-2">我是 Music Tag Web 智能助手, 我可以帮你快速了解和使用本项目。</div>

                                    <div class="mt-6 grid gap-2.5 text-left sm:grid-cols-2">
                                        <button v-for="prompt in quickPrompts" :key="prompt" type="button"
                                            class="rounded-2xl border border-base-300 bg-base-100/90 px-4 py-3.5 text-xs leading-5 text-base-content shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-base-100 hover:shadow-md"
                                            @click="applyQuickPrompt(prompt)">
                                            {{ prompt }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <footer class="floating-ai-chat-footer border-t border-base-300 bg-base-100/90 px-4 py-3 backdrop-blur md:px-5">
                            <div
                                class="floating-ai-chat-composer mx-auto rounded-[24px] border border-base-300 bg-base-100/95 p-2 shadow-sm ring-1 ring-base-200/70">
                                <textarea v-model="input" rows="3"
                                    class="textarea h-20 w-full resize-none border-0 bg-transparent p-2 text-sm leading-6 focus:outline-none"
                                    placeholder="请输入你想让 AI 帮你处理的问题..." @keydown.enter.exact="onInputEnterKey"
                                    @compositionstart="onCompositionStart" @compositionend="onCompositionEnd">
                                </textarea>
                                <div class="mt-1.5 flex items-center justify-between gap-3 px-2 pb-0.5">
                                    <div class="text-[11px] text-base-content/50">Enter 发送，Shift + Enter 换行</div>
                                    <div class="flex items-center gap-2">
                                        <button type="button" class="btn btn-ghost btn-sm rounded-full"
                                            :disabled="!chat.isCurrentSessionLoading" @click="abort">
                                            停止
                                        </button>
                                        <button type="button"
                                            class="btn btn-primary btn-sm rounded-full shadow-md shadow-primary/15"
                                            :disabled="chat.isCurrentSessionLoading" @click="sendMessage">
                                            发送
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                </div>
            </section>
            <button v-else key="trigger" type="button"
                class="launcher-trigger btn btn-circle btn-ghost h-14 w-14 items-center justify-center border border-base-200 bg-base-100/80 p-0 text-primary-600 shadow-2xl backdrop-blur md:h-[60px] md:w-[60px]"
                :class="{ 'cursor-grabbing': isDragging }"
                :title="launcherTitle" @click="openPanel" @mousedown.prevent="onDragStart" @touchstart.stop="onDragStart">
                <span v-if="!isLauncherAvailable" class="launcher-badge">V2</span>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
                    class="launcher-icon h-8 w-8 shrink-0" fill="currentColor">
                    <path
                        d="M12 2.75L13.817 8.183L19.25 10L13.817 11.817L12 17.25L10.183 11.817L4.75 10L10.183 8.183L12 2.75Z"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M18.5 15.5L19.232 17.518L21.25 18.25L19.232 18.982L18.5 21L17.768 18.982L15.75 18.25L17.768 17.518L18.5 15.5Z"
                        fill="currentColor" />
                </svg>
            </button>
        </transition>
    </div>
</template>

<script>
import { createAiChatController } from '@/vendor/ai-chat-opencode'
import { mapGetters, mapState } from 'vuex'

const STORAGE_KEY = 'mtw-opencode-demo-config'
const DEFAULT_SYSTEM = `{
  "PY-CRM-TOKEN": "LOGIN_TOKEN_demo",
  "PY-CRM-DEVICE": "music-tag-web"
}`

const DEFAULT_CONFIG = {
    opencodeUrl: '/opencode',
    opencodeDirectory: '/home/opencode/app',
    opencodeSystem: DEFAULT_SYSTEM,
    opencodeAgent: 'build',
    opencodeModelID: 'deepseek-v4-pro',
    opencodeProviderID: 'deepseek',
    opencodeAuthorization: '',
    source: 'music-tag-web-demo',
    userId: ''
}

export default {
    name: 'FloatingAiChatLauncher',
    data() {
        return {
            showPanel: false,
            showSessions: false,
            isPanelWide: false,
            panelConfig: Object.assign({}, DEFAULT_CONFIG),
            chat: this.createFallbackController(),
            input: '',
            isComposingInput: false,
            panelReady: false,
            activeConfigSignature: '',
            expandedReasoningKeys: {},
            expandedProcessPartKeys: {},
            questionToolAnswers: {},
            questionToolCustomAnswers: {},
            questionToolSubmitting: {},
            questionToolSubmitted: {},
            questionToolActiveStep: {},
            playbackStatus: {},
            playbackHandled: {},
            playbackExecutionArmed: false,
            shouldAutoScroll: true,
            dragPosition: null,
            isDragging: false,
            dragMoved: false,
            dragOffset: { x: 0, y: 0 },
            dragStartPos: { x: 0, y: 0 },
            suppressClickUntil: 0,
            quickPrompts: [
                '怎么扫描导入本地音乐文件夹？',
                '修改音乐库中同一张专辑被拆分成多张的专辑。',
                '怎么设置后台自动刮削入库整理音乐？',
                '如何用手机 App 连接本系统播放音乐？'
            ]
        }
    },
    computed: {
        ...mapState({
            sysDict: state => state.common.sysDict || {}
        }),
        ...mapGetters(['getUsername', 'getUserRole']),
        wrapperStyle() {
            if (!this.showPanel && this.dragPosition) {
                return {
                    right: 'auto',
                    bottom: 'auto',
                    left: this.dragPosition.x + 'px',
                    top: this.dragPosition.y + 'px'
                }
            }
            return {
                right: '1rem'
            }
        },
        launcherEnabled() {
            return this.normalizeBoolean(this.sysDict.opencode_show_launcher)
        },
        shouldShowLauncher() {
            return this.$route.name !== 'login' && this.launcherEnabled
        },
        isLauncherAvailable() {
            return this.getUserRole === 'V2'
        },
        launcherTitle() {
            return this.isLauncherAvailable ? '打开 AI 助手' : '智能助手是 V2 功能'
        },
        panelSizeTitle() {
            return this.isPanelWide ? '恢复默认宽度' : '放大宽度'
        },
        customQuestionOptionValue() {
            return '__custom_question_answer__'
        },
        resolvedUserId() {
            if (this.getUsername) {
                return 'mtw_' + this.getUsername
            }
            if (typeof this.getCookie === 'function') {
                const cookieUser = this.getCookie('username')
                if (cookieUser) {
                    return 'mtw_' + cookieUser
                }
            }
            return 'mtw_demo_user'
        },
        effectiveUserId() {
            return this.panelConfig.userId || this.resolvedUserId
        },
        activeSessionLabel() {
            if (!this.chat.activeSessionId) {
                return '新草稿'
            }
            const current = this.chat.sessionList.find(item => item.id === this.chat.activeSessionId)
            return current ? this.chat.getSessionTitle(current) : this.chat.activeSessionId
        },
        backendPanelConfig() {
            return {
                opencodeUrl: this.sysDict.opencode_url,
                opencodeDirectory: this.sysDict.opencode_directory,
                opencodeSystem: this.sysDict.opencode_system,
                opencodeAgent: this.sysDict.opencode_agent,
                opencodeModelID: this.sysDict.opencode_model_id,
                opencodeProviderID: this.sysDict.opencode_provider_id,
                opencodeAuthorization: this.sysDict.opencode_authorization,
                source: this.sysDict.opencode_source,
                userId: this.sysDict.opencode_user_id
            }
        }
    },
    watch: {
        'chat.messages': {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.scrollToBottom()
                    if (this.playbackExecutionArmed) {
                        this.executeLatestPlaybackCommand()
                    }
                })
            }
        },
        'chat.isCurrentSessionLoading'(loading, previousLoading) {
            this.$nextTick(() => {
                this.scrollToBottom()
                // promptAsync 提交成功后会立即返回，只有生成状态结束时才代表回复内容完整。
                if (previousLoading && !loading && this.playbackExecutionArmed) {
                    this.executeLatestPlaybackCommand()
                    this.playbackExecutionArmed = false
                }
            })
        },
        launcherEnabled(value) {
            if (!value) {
                this.showPanel = false
                this.showSessions = false
            }
        },
        isLauncherAvailable(value) {
            if (!value) {
                this.showPanel = false
                this.showSessions = false
            }
        }
    },
    mounted() {
        this.loadDragPosition()
        window.addEventListener('resize', this.handleViewportResize)
    },
    beforeDestroy() {
        this.disposeChat()
        this.removeDragListeners()
        window.removeEventListener('resize', this.handleViewportResize)
    },
    methods: {
        normalizeBoolean(value) {
            if (typeof value === 'boolean') {
                return value
            }
            if (typeof value === 'number') {
                return value === 1
            }
            if (typeof value === 'string') {
                return value === '1' || value.toLowerCase() === 'true'
            }
            return false
        },
        getDragBounds() {
            const launcherWidth = 80
            const launcherHeight = 100
            return {
                maxX: Math.max(0, window.innerWidth - launcherWidth),
                maxY: Math.max(0, window.innerHeight - launcherHeight)
            }
        },
        clampDragPosition(position) {
            if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
                return null
            }
            const { maxX, maxY } = this.getDragBounds()
            return {
                x: Math.max(0, Math.min(position.x, maxX)),
                y: Math.max(0, Math.min(position.y, maxY))
            }
        },
        loadDragPosition() {
            try {
                const saved = window.localStorage.getItem('mtw-ai-launcher-pos')
                if (saved) {
                    const pos = JSON.parse(saved)
                    const nextPosition = this.clampDragPosition(pos)
                    if (nextPosition) {
                        this.dragPosition = nextPosition
                        if (nextPosition.x !== pos.x || nextPosition.y !== pos.y) {
                            this.saveDragPosition()
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
        },
        saveDragPosition() {
            if (this.dragPosition) {
                try {
                    window.localStorage.setItem('mtw-ai-launcher-pos', JSON.stringify(this.dragPosition))
                } catch (e) {
                    // ignore
                }
            }
        },
        resetPosition() {
            this.dragPosition = null
            try {
                window.localStorage.removeItem('mtw-ai-launcher-pos')
            } catch (e) {
                // ignore
            }
        },
        handleViewportResize() {
            if (!this.dragPosition) {
                return
            }
            const nextPosition = this.clampDragPosition(this.dragPosition)
            if (!nextPosition) {
                this.resetPosition()
                return
            }
            if (nextPosition.x !== this.dragPosition.x || nextPosition.y !== this.dragPosition.y) {
                this.dragPosition = nextPosition
                this.saveDragPosition()
            }
        },
        onDragStart(event) {
            if (this.showPanel) return
            const clientX = event.touches ? event.touches[0].clientX : event.clientX
            const clientY = event.touches ? event.touches[0].clientY : event.clientY
            const rect = this.$el.getBoundingClientRect()
            this.dragOffset = {
                x: clientX - rect.left,
                y: clientY - rect.top
            }
            this.dragStartPos = { x: clientX, y: clientY }
            this.dragMoved = false
            this.isDragging = true
            document.addEventListener('mousemove', this.onDragMove)
            document.addEventListener('mouseup', this.onDragEnd)
            document.addEventListener('touchmove', this.onDragMove, { passive: false })
            document.addEventListener('touchend', this.onDragEnd)
            if (!event.touches && event.cancelable) {
                event.preventDefault()
            }
        },
        onDragMove(event) {
            if (!this.isDragging) return
            const clientX = event.touches ? event.touches[0].clientX : event.clientX
            const clientY = event.touches ? event.touches[0].clientY : event.clientY
            const dx = clientX - this.dragStartPos.x
            const dy = clientY - this.dragStartPos.y
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                this.dragMoved = true
            }
            this.dragPosition = this.clampDragPosition({
                x: clientX - this.dragOffset.x,
                y: clientY - this.dragOffset.y
            })
            if (event.cancelable) event.preventDefault()
        },
        onDragEnd() {
            const wasDragged = this.dragMoved
            this.isDragging = false
            this.removeDragListeners()
            this.saveDragPosition()
            if (wasDragged) {
                this.suppressClickUntil = Date.now() + 350
            }
            setTimeout(() => {
                this.dragMoved = false
            }, 50)
        },
        removeDragListeners() {
            document.removeEventListener('mousemove', this.onDragMove)
            document.removeEventListener('mouseup', this.onDragEnd)
            document.removeEventListener('touchmove', this.onDragMove)
            document.removeEventListener('touchend', this.onDragEnd)
        },
        createFallbackController() {
            return {
                sessionList: [],
                activeSessionId: null,
                messages: [],
                isCurrentSessionLoading: false,
                hasCurrentSessionReplyContent: false,
                getSessionTitle(session) {
                    return session && session.title ? session.title : '未命名会话'
                },
                isSessionGenerating() {
                    return false
                },
                renderMarkdownHtml(text) {
                    return String(text || '')
                }
            }
        },
        loadPanelConfig() {
            let localConfig = {}
            try {
                const raw = window.localStorage.getItem(STORAGE_KEY)
                if (raw) {
                    localConfig = JSON.parse(raw)
                    delete localConfig.opencodeAuthorization
                }
            } catch (error) {
                console.warn('读取 AI 助手配置失败:', error)
            }
            this.panelConfig = Object.assign({}, DEFAULT_CONFIG, localConfig, this.getAvailableBackendConfig())
        },
        getAvailableBackendConfig() {
            const backendConfig = {}
            Object.keys(this.backendPanelConfig).forEach((key) => {
                const value = this.backendPanelConfig[key]
                if (value !== undefined && value !== null && String(value).trim() !== '') {
                    backendConfig[key] = value
                }
            })
            return backendConfig
        },
        buildConfigSignature(config) {
            return JSON.stringify({
                opencodeUrl: config.opencodeUrl,
                opencodeDirectory: config.opencodeDirectory,
                opencodeSystem: config.opencodeSystem,
                opencodeAgent: config.opencodeAgent,
                opencodeModelID: config.opencodeModelID,
                opencodeProviderID: config.opencodeProviderID,
                opencodeAuthorization: config.opencodeAuthorization,
                source: config.source,
                userId: this.effectiveUserId
            })
        },
        buildOpencodeHeaders(config) {
            const authorization = config && config.opencodeAuthorization
                ? String(config.opencodeAuthorization).trim()
                : ''
            return authorization ? { Authorization: authorization } : {}
        },
        async ensureChatReady() {
            this.loadPanelConfig()
            const nextSignature = this.buildConfigSignature(this.panelConfig)
            if (this.panelReady && this.activeConfigSignature === nextSignature) {
                return
            }
            this.disposeChat()
            const controller = createAiChatController({
                opencodeUrl: this.panelConfig.opencodeUrl,
                opencodeDirectory: this.panelConfig.opencodeDirectory,
                opencodeSystem: this.panelConfig.opencodeSystem,
                opencodeAgent: this.panelConfig.opencodeAgent,
                opencodeModelID: this.panelConfig.opencodeModelID,
                opencodeProviderID: this.panelConfig.opencodeProviderID,
                opencodeHeaders: this.buildOpencodeHeaders(this.panelConfig),
                source: this.panelConfig.source,
                userId: this.effectiveUserId,
                maxSessionCount: 10,
                initialMessages: [
                ],
                toast: this.customToast,
                confirm: this.customConfirm,
                onError: this.onControllerError
            })
            this.chat = controller
            await controller.init()
            this.panelReady = true
            this.activeConfigSignature = nextSignature
            this.shouldAutoScroll = true
            this.$nextTick(() => {
                this.scrollToBottom(true)
            })
        },
        disposeChat() {
            if (this.chat && typeof this.chat.dispose === 'function') {
                this.chat.dispose()
            }
            this.panelReady = false
            this.activeConfigSignature = ''
        },
        async openPanel() {
            if (Date.now() < this.suppressClickUntil) return
            if (this.dragMoved) return
            if (!this.isLauncherAvailable) {
                this.customToast({
                    message: '智能助手仅对 V2 用户开放。',
                    type: 'warning'
                })
                return
            }
            this.showPanel = true
            try {
                await this.ensureChatReady()
                this.showSessions = false
            } catch (error) {
                this.showPanel = false
                this.customToast({
                    message: '初始化 AI 助手失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        closePanel() {
            this.showPanel = false
        },
        togglePanelWide() {
            this.isPanelWide = !this.isPanelWide
        },
        onCompositionStart() {
            this.isComposingInput = true
        },
        onCompositionEnd() {
            this.isComposingInput = false
        },
        onInputEnterKey(event) {
            if (!event) {
                return
            }
            if (this.isComposingInput || event.isComposing || event.keyCode === 229) {
                return
            }
            event.preventDefault()
            this.sendMessage()
        },
        async sendMessage() {
            const text = this.input.trim()
            if (!text || !this.chat || this.chat.isCurrentSessionLoading) {
                return
            }
            if (!this.isLauncherAvailable) {
                this.customToast({
                    message: '智能助手仅对 V2 用户开放。',
                    type: 'warning'
                })
                return
            }
            try {
                this.playbackExecutionArmed = true
                this.shouldAutoScroll = true
                await this.chat.sendMessage(text)
                this.input = ''
                this.$nextTick(() => {
                    this.scrollToBottom(true)
                })
            } catch (error) {
                this.playbackExecutionArmed = false
                this.customToast({
                    message: error && error.message ? error.message : '发送失败',
                    type: 'error'
                })
            }
        },
        applyQuickPrompt(prompt) {
            this.input = prompt
            this.$nextTick(() => {
                this.sendMessage()
            })
        },
        getPlaybackStatus(message) {
            const key = this.getPlaybackMessageKey(message)
            return key ? (this.playbackStatus[key] || '') : ''
        },
        getPlaybackCommand(message) {
            return message && message.role === 'assistant'
                ? this.extractPlaybackCommand(message.content)
                : null
        },
        getPlaybackTrack(message) {
            const command = this.getPlaybackCommand(message)
            return command && Array.isArray(command.tracks) ? command.tracks[0] || {} : {}
        },
        getPlaybackTitle(message) {
            const command = this.getPlaybackCommand(message)
            const track = this.getPlaybackTrack(message)
            if (command && command.action !== 'play') {
                return this.getPlaybackActionLabel(message)
            }
            return track.title || track.name || '未知歌曲'
        },
        getPlaybackArtist(message) {
            const track = this.getPlaybackTrack(message)
            const parts = [track.artist, track.album].filter(value => value)
            return parts.join(' · ') || '音乐库歌曲'
        },
        getPlaybackCover(message) {
            const currentTrack = this.$store.getters.track
            const commandTrack = this.getPlaybackTrack(message)
            if (currentTrack && String(currentTrack.id) === String(commandTrack.id) && currentTrack.image) {
                return currentTrack.image
            }
            return '/static/dist/img/album_null.jpg'
        },
        getPlaybackActionLabel(message) {
            const command = this.getPlaybackCommand(message) || {}
            const labels = {
                play: command.queue_mode === 'append' ? '加入播放队列' : '正在播放',
                pause: '暂停播放',
                stop: '停止播放',
                next: '下一首',
                previous: '上一首'
            }
            return labels[command.action] || '音乐播放'
        },
        isPlaybackCardPlaying(message) {
            const command = this.getPlaybackCommand(message)
            const track = this.getPlaybackTrack(message)
            const currentTrack = this.$store.getters.track
            return Boolean(
                command && command.action === 'play' &&
                this.$store.state.common.isPlaying &&
                currentTrack && track.id != null && String(currentTrack.id) === String(track.id)
            )
        },
        async handlePlaybackCardAction(message) {
            const command = this.getPlaybackCommand(message)
            if (!command) {
                return
            }
            try {
                if (this.isPlaybackCardPlaying(message)) {
                    await this.$store.dispatch('pause')
                    return
                }
                const currentTrack = this.$store.getters.track
                const track = this.getPlaybackTrack(message)
                if (currentTrack && track.id != null && String(currentTrack.id) === String(track.id)) {
                    await this.$store.dispatch('resume')
                } else {
                    await this.executePlaybackCommand(command)
                }
            } catch (error) {
                this.customToast({
                    message: '播放失败：' + (error && error.message ? error.message : '无法执行播放指令'),
                    type: 'error'
                })
            }
        },
        async handlePlaybackNext() {
            try {
                await this.$store.dispatch('next')
            } catch (error) {
                this.customToast({
                    message: '切换下一首失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        getPlaybackMessageKey(message) {
            if (!message) {
                return ''
            }
            return message.messageId || message.id || ('content:' + String(message.content || ''))
        },
        extractPlaybackCommand(content) {
            const text = String(content || '').trim()
            if (!text) {
                return null
            }
            const candidates = []
            const fenced = text.match(/```json\s*([\s\S]*?)\s*```/i)
            if (fenced) {
                candidates.push(fenced[1])
            }
            // 兼容某些消息适配层把换行和双引号再次转义的情况。
            if (fenced) {
                candidates.push(fenced[1].replace(/\\n/g, '\n').replace(/\\"/g, '"'))
            }
            candidates.push(text)
            for (let index = 0; index < candidates.length; index++) {
                try {
                    const value = JSON.parse(candidates[index].trim())
                    if (value && value.type === 'music_playback') {
                        return value
                    }
                } catch (error) {
                    // 普通回答不是播放指令，继续尝试其他候选文本。
                }
            }
            return null
        },
        async executeLatestPlaybackCommand() {
            const messages = this.chat && Array.isArray(this.chat.messages) ? this.chat.messages : []
            const message = [...messages].reverse().find(item => item && item.role === 'assistant' && item.content)
            if (!message) {
                return
            }
            const key = this.getPlaybackMessageKey(message)
            if (!key || this.playbackHandled[key]) {
                return
            }
            const command = this.extractPlaybackCommand(message.content)
            if (!command) {
                return
            }
            this.$set(this.playbackHandled, key, true)
            try {
                await this.executePlaybackCommand(command)
                this.$set(this.playbackStatus, key, command.message || '播放指令已执行')
            } catch (error) {
                this.$set(this.playbackStatus, key, '播放失败：' + (error && error.message ? error.message : '无法执行播放指令'))
                this.customToast({
                    message: '播放失败：' + (error && error.message ? error.message : '无法执行播放指令'),
                    type: 'error'
                })
            }
        },
        async executePlaybackCommand(command) {
            const action = String(command.action || '').toLowerCase()
            if (action === 'pause') {
                await this.$store.dispatch('pause')
                return
            }
            if (action === 'stop') {
                await this.$store.dispatch('resetQueue')
                return
            }
            if (action === 'next') {
                await this.$store.dispatch('next')
                return
            }
            if (action === 'previous') {
                await this.$store.dispatch('previous')
                return
            }
            if (action !== 'play') {
                throw new Error('不支持的播放动作')
            }

            const rawTracks = Array.isArray(command.tracks) ? command.tracks : []
            if (!rawTracks.length) {
                throw new Error('播放指令没有歌曲')
            }
            const ids = rawTracks.map(track => track && track.id).filter(id => id !== undefined && id !== null && String(id).trim() !== '')
            if (!ids.length || ids.length !== rawTracks.length) {
                throw new Error('播放指令中的歌曲 ID 无效')
            }

            // 只按 ID 从当前登录的音乐库重新取完整歌曲对象，URL 和封面由现有 API 生成。
            const tracks = []
            for (let index = 0; index < ids.length; index++) {
                const track = await this.$api.subsonic.getSong(ids[index])
                if (!track || track.id === undefined || track.id === null || !track.url) {
                    throw new Error('歌曲不存在或无法播放')
                }
                tracks.push(track)
            }

            const queueMode = ['replace', 'append', 'next'].includes(command.queue_mode)
                ? command.queue_mode
                : 'replace'
            const autoplay = command.autoplay !== false
            if (queueMode === 'append') {
                await this.$store.dispatch('player/addToQueue', tracks)
                if (autoplay && !this.$store.getters.track) {
                    await this.$store.dispatch('playTrackList', { tracks, index: 0 })
                }
                return
            }
            if (queueMode === 'next') {
                if (!this.$store.getters.track) {
                    await this.$store.dispatch('playTrackList', { tracks, index: 0 })
                } else {
                    await this.$store.dispatch('player/setNextInQueue', tracks)
                }
                return
            }
            if (autoplay) {
                await this.$store.dispatch('playTrackList', { tracks, index: 0 })
            } else {
                this.$store.commit('setQueue', tracks)
            }
        },
        abort() {
            if (this.chat && typeof this.chat.abort === 'function') {
                this.chat.abort()
            }
        },
        async selectSession(sessionId) {
            if (!this.chat || !sessionId) {
                return
            }
            try {
                this.shouldAutoScroll = true
                await this.chat.selectSession(sessionId)
                this.$nextTick(() => {
                    this.scrollToBottom(true)
                })
            } catch (error) {
                this.customToast({
                    message: '切换会话失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        async deleteSession(sessionId) {
            if (!this.chat || !sessionId) {
                return
            }
            try {
                await this.chat.deleteSession(sessionId)
            } catch (error) {
                this.customToast({
                    message: '删除会话失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        startNewDraft() {
            if (this.chat && typeof this.chat.startNewDraft === 'function') {
                this.chat.startNewDraft()
                this.showSessions = false
            }
        },
        async reloadMessages() {
            if (!this.chat || !this.chat.activeSessionId) {
                return
            }
            try {
                await this.chat.reloadMessages()
            } catch (error) {
                this.customToast({
                    message: '刷新历史失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            }
        },
        shortSessionId(value) {
            if (!value) {
                return ''
            }
            return String(value).slice(0, 6)
        },
        getReasoningKey(message, index) {
            return message.messageId || ('reasoning-' + index)
        },
        isReasoningExpanded(message, index) {
            if (this.isReasoningStreaming(message, index)) {
                return true
            }
            return Boolean(this.expandedReasoningKeys[this.getReasoningKey(message, index)])
        },
        isReasoningStreaming(message, index) {
            return Boolean(
                this.chat &&
                this.chat.isCurrentSessionLoading &&
                message &&
                message.role === 'assistant' &&
                message.reasoning &&
                !message.content &&
                index === this.chat.messages.length - 1
            )
        },
        hasProcessParts(message) {
            return Boolean(message && Array.isArray(message.processParts) && message.processParts.length)
        },
        isQuestionToolPart(part) {
            return Boolean(part && part.type === 'tool' && String(part.tool || '') === 'question')
        },
        getQuestionToolQuestions(part) {
            const state = part && part.state && typeof part.state === 'object' ? part.state : null
            const input = state && state.input && typeof state.input === 'object' ? state.input : null
            if (input && Array.isArray(input.questions)) {
                return input.questions
            }
            const request = this.getQuestionRequestByCallId(part)
            return request && Array.isArray(request.questions) ? request.questions : []
        },
        getQuestionOptions(questionItem) {
            return questionItem && Array.isArray(questionItem.options) ? questionItem.options : []
        },
        getQuestionOptionValue(option) {
            if (!option) {
                return ''
            }
            return option.value != null ? String(option.value) : String(option.label || '')
        },
        shouldRenderProcessPart(part) {
            if (!part) {
                return false
            }
            if (this.isQuestionToolPart(part) && this.getQuestionToolQuestions(part).length) {
                return true
            }
            const summary = this.getProcessPartSummary(part, false).trim()
            return Boolean(summary)
        },
        getVisibleProcessParts(message) {
            if (!this.hasProcessParts(message)) {
                return []
            }
            return message.processParts.filter((part) => {
                return this.shouldRenderProcessPart(part) && !this.isQuestionToolPart(part)
            })
        },
        hasVisibleProcessParts(message) {
            return this.getVisibleProcessParts(message).length > 0
        },
        getDisplayParts(message) {
            if (!message || !Array.isArray(message.displayParts)) {
                return []
            }
            return message.displayParts.filter((part) => {
                if (!part) {
                    return false
                }
                if (part.type === 'text') {
                    return Boolean(String(part.text || '').trim())
                }
                return part.type === 'process' && this.isQuestionToolPart(part.part) && this.shouldRenderProcessPart(part.part)
            })
        },
        hasDisplayParts(message) {
            return this.getDisplayParts(message).length > 0
        },
        getDisplayPartKey(message, index, displayPart, displayPartIndex) {
            const messageKey = this.getReasoningKey(message, index)
            if (displayPart && displayPart.type === 'process') {
                const part = displayPart.part || {}
                return messageKey + ':display:process:' + (part.id || part.callID || displayPartIndex)
            }
            return messageKey + ':display:text:' + displayPartIndex
        },
        getProcessPartKey(message, index, part, partIndex) {
            const messageKey = this.getReasoningKey(message, index)
            return messageKey + ':process:' + (part && part.id ? part.id : partIndex)
        },
        getQuestionToolKey(message, index, part, partIndex) {
            const messageKey = this.getReasoningKey(message, index)
            return messageKey + ':question:' + ((part && part.callID) || this.getQuestionRequestId(part) || (part && part.id) || partIndex)
        },
        isQuestionRequestId(value) {
            return typeof value === 'string' && value.indexOf('que') === 0
        },
        getQuestionRequestByCallId(part) {
            const callID = part && part.callID ? String(part.callID) : ''
            if (!callID || !this.chat || !this.chat.questionRequestsByCallId) {
                return null
            }
            return this.chat.questionRequestsByCallId[callID] || null
        },
        getQuestionRequestId(part) {
            const state = part && part.state && typeof part.state === 'object' ? part.state : null
            const input = state && state.input && typeof state.input === 'object' ? state.input : null
            const raw = part && part.raw && typeof part.raw === 'object' ? part.raw : null
            const request = this.getQuestionRequestByCallId(part)
            const candidates = [
                request && request.requestID,
                state && (state.requestID || state.requestId || state.id),
                input && (input.requestID || input.requestId || input.id),
                raw && (raw.requestID || raw.requestId)
            ]
            return candidates.find((value) => this.isQuestionRequestId(value)) || ''
        },
        getQuestionToolStatusText(part) {
            if (!this.getQuestionRequestId(part)) {
                return '等待问题请求编号'
            }
            const status = this.getProcessPartStatus(part)
            return status ? '状态：' + status : '请选择后提交'
        },
        getQuestionActiveStep(message, index, part, partIndex) {
            const key = this.getQuestionToolKey(message, index, part, partIndex)
            const questions = this.getQuestionToolQuestions(part)
            const current = Number(this.questionToolActiveStep[key] || 0)
            if (!questions.length) {
                return 0
            }
            if (current < 0) {
                return 0
            }
            if (current >= questions.length) {
                return questions.length - 1
            }
            return current
        },
        setQuestionActiveStep(message, index, part, partIndex, questionIndex) {
            if (!this.canOpenQuestionStep(message, index, part, partIndex, questionIndex)) {
                return
            }
            this.$set(this.questionToolActiveStep, this.getQuestionToolKey(message, index, part, partIndex), questionIndex)
        },
        getActiveQuestionItem(message, index, part, partIndex) {
            const questions = this.getQuestionToolQuestions(part)
            return questions[this.getQuestionActiveStep(message, index, part, partIndex)] || null
        },
        canLeaveQuestionStep(message, index, part, partIndex, questionIndex) {
            return Boolean(this.getQuestionToolAnswer(message, index, part, partIndex, questionIndex))
        },
        canOpenQuestionStep(message, index, part, partIndex, questionIndex) {
            if (questionIndex <= 0) {
                return true
            }
            const activeStep = this.getQuestionActiveStep(message, index, part, partIndex)
            if (questionIndex <= activeStep) {
                return true
            }
            for (let i = 0; i < questionIndex; i++) {
                if (!this.canLeaveQuestionStep(message, index, part, partIndex, i)) {
                    return false
                }
            }
            return true
        },
        isLastQuestionStep(message, index, part, partIndex) {
            const questions = this.getQuestionToolQuestions(part)
            return this.getQuestionActiveStep(message, index, part, partIndex) >= questions.length - 1
        },
        goPrevQuestionStep(message, index, part, partIndex) {
            const activeStep = this.getQuestionActiveStep(message, index, part, partIndex)
            this.setQuestionActiveStep(message, index, part, partIndex, Math.max(activeStep - 1, 0))
        },
        goNextQuestionStep(message, index, part, partIndex) {
            const activeStep = this.getQuestionActiveStep(message, index, part, partIndex)
            if (!this.canLeaveQuestionStep(message, index, part, partIndex, activeStep)) {
                return
            }
            this.setQuestionActiveStep(message, index, part, partIndex, activeStep + 1)
        },
        getQuestionToolAnswerKey(message, index, part, partIndex, questionIndex) {
            return this.getQuestionToolKey(message, index, part, partIndex) + ':' + questionIndex
        },
        getQuestionCustomAnswerKey(message, index, part, partIndex, questionIndex) {
            return this.getQuestionToolKey(message, index, part, partIndex) + ':custom:' + questionIndex
        },
        setQuestionToolAnswer(message, index, part, partIndex, questionIndex, option) {
            const key = this.getQuestionToolAnswerKey(message, index, part, partIndex, questionIndex)
            this.$set(this.questionToolAnswers, key, this.getQuestionOptionValue(option))
        },
        setQuestionCustomSelected(message, index, part, partIndex, questionIndex) {
            const key = this.getQuestionToolAnswerKey(message, index, part, partIndex, questionIndex)
            this.$set(this.questionToolAnswers, key, this.customQuestionOptionValue)
        },
        setQuestionCustomAnswer(message, index, part, partIndex, questionIndex, value) {
            const key = this.getQuestionCustomAnswerKey(message, index, part, partIndex, questionIndex)
            this.$set(this.questionToolCustomAnswers, key, value != null ? String(value) : '')
        },
        getQuestionCustomAnswer(message, index, part, partIndex, questionIndex) {
            return this.questionToolCustomAnswers[this.getQuestionCustomAnswerKey(message, index, part, partIndex, questionIndex)] || ''
        },
        getQuestionToolAnswer(message, index, part, partIndex, questionIndex) {
            const answer = this.questionToolAnswers[this.getQuestionToolAnswerKey(message, index, part, partIndex, questionIndex)] || ''
            if (answer === this.customQuestionOptionValue) {
                return this.getQuestionCustomAnswer(message, index, part, partIndex, questionIndex).trim()
            }
            return answer
        },
        getQuestionSelectedValue(message, index, part, partIndex, questionIndex) {
            return this.questionToolAnswers[this.getQuestionToolAnswerKey(message, index, part, partIndex, questionIndex)] || ''
        },
        isQuestionOptionSelected(message, index, part, partIndex, questionIndex, option) {
            return this.getQuestionSelectedValue(message, index, part, partIndex, questionIndex) === this.getQuestionOptionValue(option)
        },
        isQuestionCustomSelected(message, index, part, partIndex, questionIndex) {
            return this.getQuestionSelectedValue(message, index, part, partIndex, questionIndex) === this.customQuestionOptionValue
        },
        isQuestionToolBusy(message, index, part, partIndex) {
            return Boolean(this.questionToolSubmitting[this.getQuestionToolKey(message, index, part, partIndex)])
        },
        isQuestionToolSubmitted(message, index, part, partIndex) {
            const request = this.getQuestionRequestByCallId(part)
            return Boolean(
                this.questionToolSubmitted[this.getQuestionToolKey(message, index, part, partIndex)] ||
                (part && part.status === 'completed') ||
                (request && (request.status === 'completed' || request.status === 'rejected'))
            )
        },
        canSubmitQuestionTool(message, index, part, partIndex) {
            const questions = this.getQuestionToolQuestions(part)
            if (!questions.length || !this.getQuestionRequestId(part)) {
                return false
            }
            return questions.every((questionItem, questionIndex) => {
                return Boolean(this.getQuestionToolAnswer(message, index, part, partIndex, questionIndex))
            })
        },
        getQuestionToolAnswers(message, index, part, partIndex) {
            return this.getQuestionToolQuestions(part).map((questionItem, questionIndex) => {
                return [this.getQuestionToolAnswer(message, index, part, partIndex, questionIndex)]
            })
        },
        async submitQuestionTool(message, index, part, partIndex) {
            if (!this.canSubmitQuestionTool(message, index, part, partIndex) || !this.chat || !this.chat.opencodeClient) {
                return
            }
            const key = this.getQuestionToolKey(message, index, part, partIndex)
            this.$set(this.questionToolSubmitting, key, true)
            try {
                await this.chat.opencodeClient.question.reply({
                    requestID: this.getQuestionRequestId(part),
                    answers: this.getQuestionToolAnswers(message, index, part, partIndex)
                })
                this.$set(this.questionToolSubmitted, key, true)
            } catch (error) {
                this.customToast({
                    message: '提交问题答案失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            } finally {
                this.$set(this.questionToolSubmitting, key, false)
            }
        },
        async rejectQuestionTool(message, index, part, partIndex) {
            if (!this.getQuestionRequestId(part) || !this.chat || !this.chat.opencodeClient) {
                return
            }
            const key = this.getQuestionToolKey(message, index, part, partIndex)
            this.$set(this.questionToolSubmitting, key, true)
            try {
                await this.chat.opencodeClient.question.reject({
                    requestID: this.getQuestionRequestId(part)
                })
                this.$set(this.questionToolSubmitted, key, true)
            } catch (error) {
                this.customToast({
                    message: '拒绝问题失败：' + (error && error.message ? error.message : '未知错误'),
                    type: 'error'
                })
            } finally {
                this.$set(this.questionToolSubmitting, key, false)
            }
        },
        isProcessPartExpanded(message, index, part, partIndex) {
            return Boolean(this.expandedProcessPartKeys[this.getProcessPartKey(message, index, part, partIndex)])
        },
        toggleProcessPart(message, index, part, partIndex) {
            const key = this.getProcessPartKey(message, index, part, partIndex)
            this.$set(this.expandedProcessPartKeys, key, !this.expandedProcessPartKeys[key])
        },
        getProcessPartLabel(part) {
            const type = part && part.type ? String(part.type) : 'unknown'
            const tool = part && part.tool ? String(part.tool) : ''
            const isChild = !!(part && part.sourceSessionId)
            if (type === 'tool') {
                return isChild
                    ? (tool ? '子任务工具: ' + tool : '子任务工具')
                    : (tool ? '工具调用: ' + tool : '工具调用')
            }
            if (type === 'step-start') {
                return isChild ? '子任务步骤开始' : '步骤开始'
            }
            if (type === 'step-finish') {
                return isChild ? '子任务步骤完成' : '步骤完成'
            }
            if (type === 'subsession-text') {
                return '子任务输出'
            }
            if (type === 'subsession-reasoning') {
                return '子任务思考'
            }
            if (type === 'subsession-status') {
                return '子任务状态'
            }
            return isChild ? '子任务事件: ' + type : type
        },
        getProcessPartStatus(part) {
            const status = part && part.status ? String(part.status) : ''
            if (!status) {
                return ''
            }
            if (status === 'running') return '进行中'
            if (status === 'completed') return '已完成'
            if (status === 'pending') return '等待中'
            if (status === 'failed') return '失败'
            return status
        },
        getProcessPartPreview(part) {
            if (!part) {
                return ''
            }
            const text = this.getProcessPartSummary(part, false).split('\n')[0] || ''
            return text || '查看详情'
        },
        getProcessPartSummary(part, withHeader = false) {
            if (!part) {
                return ''
            }
            const header = withHeader
                ? '[' + this.getProcessPartLabel(part) + (this.getProcessPartStatus(part) ? ' / ' + this.getProcessPartStatus(part) : '') + ']'
                : ''
            if (part.type === 'tool') {
                const state = part.state && typeof part.state === 'object' ? part.state : null
                const lines = []
                if (header) {
                    lines.push(header)
                }
                if (state && state.title) {
                    lines.push(state.title)
                }
                if (state && state.input && Object.keys(state.input).length) {
                    const inputSummary = state.input.description || state.input.title || ''
                    lines.push('input: ' + (inputSummary || JSON.stringify(state.input)))
                }
                if (state && state.output) {
                    lines.push('output: ' + String(state.output))
                }
                return lines.join('\n')
            }
            const lines = []
            if (header) {
                lines.push(header)
            }
            if (part.text) {
                lines.push(String(part.text))
                return lines.join('\n')
            }
            if (part.state && part.state.raw) {
                lines.push(String(part.state.raw))
                return lines.join('\n')
            }
            return lines.join('\n')
        },
        toggleReasoning(message, index) {
            const key = this.getReasoningKey(message, index)
            this.$set(this.expandedReasoningKeys, key, !this.expandedReasoningKeys[key])
        },
        handleMessageScroll() {
            const scroller = this.$refs.messageScroller
            if (!scroller) {
                return
            }
            const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight
            this.shouldAutoScroll = distanceFromBottom <= 64
        },
        scrollToBottom(force = false) {
            const scroller = this.$refs.messageScroller
            if (!scroller || (!force && !this.shouldAutoScroll)) {
                return
            }
            scroller.scrollTop = scroller.scrollHeight
        },
        customToast(payload) {
            const options = typeof payload === 'string' ? { message: payload } : (payload || {})
            const message = options.message || '收到通知'
            const type = ['success', 'error', 'warning'].includes(options.type) ? options.type : 'warning'
            if (this.$myMsg && this.$myMsg.notify) {
                this.$myMsg.notify({
                    title: 'AI 助手',
                    content: message,
                    type,
                    time: 2500
                })
                return
            }
            window.alert(message)
        },
        customConfirm(options) {
            const text = (options && options.title ? options.title + '\n' : '') + (options && options.message ? options.message : '确定继续吗？')
            return Promise.resolve(window.confirm(text))
        },
        onControllerError(error) {
            console.error('Floating AI chat error:', error)
        }
    }
}
</script>

<style scoped>
.floating-ai-chat-launcher {
    bottom: 1rem;
    z-index: 10000;
}

.floating-ai-chat-panel {
    width: min(396px, calc(100vw - 1.5rem));
    transition: width 180ms ease;
}

.floating-ai-chat-panel.is-wide {
    width: min(1180px, calc(100vw - 2rem));
    height: min(900px, calc(100dvh - 2.5rem));
    border-radius: 28px;
    background:
        radial-gradient(circle at 18% 0%, oklch(var(--p) / 0.12), transparent 24rem),
        linear-gradient(135deg, oklch(var(--b1)), color-mix(in oklch, oklch(var(--b2)) 84%, oklch(var(--p)) 6%));
}

.floating-ai-chat-sidebar.is-wide {
    display: none;
}

.floating-ai-chat-sidebar {
    width: 236px;
}

.floating-ai-chat-sidebar.is-session-drawer {
    display: flex;
}

.floating-ai-chat-sidebar-prompts,
.floating-ai-chat-header-hint {
    display: none;
}

.floating-ai-chat-panel.is-wide .floating-ai-chat-main {
    background:
        linear-gradient(180deg, oklch(var(--b1) / 0.92), oklch(var(--b1) / 0.98)),
        oklch(var(--b1));
}

.floating-ai-chat-panel.is-wide .floating-ai-chat-scroller {
    padding-left: clamp(1rem, 3vw, 2rem);
    padding-right: clamp(1rem, 3vw, 2rem);
}

.floating-ai-chat-thread,
.floating-ai-chat-composer {
    width: 100%;
}

.floating-ai-chat-panel.is-wide .floating-ai-chat-thread,
.floating-ai-chat-panel.is-wide .floating-ai-chat-composer {
    max-width: 780px;
}

.floating-ai-chat-panel.is-wide .floating-ai-chat-message {
    max-width: min(86%, 660px);
}

.floating-ai-chat-panel.is-wide .floating-ai-chat-footer {
    padding-top: 1rem;
    padding-bottom: 1rem;
}

.launcher-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    cursor: grab;
    user-select: none;
}

.launcher-trigger:active {
    cursor: grabbing;
}

.launcher-trigger svg {
    display: block;
}

.launcher-icon {
    transform: translate(0px, 2.25px);
}

.launcher-badge {
    position: absolute;
    left: -0.25rem;
    top: -0.25rem;
    border-radius: 9999px;
    background: hsl(var(--wa));
    color: hsl(var(--wac));
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 0.2rem 0.35rem;
}

.ai-playback-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: min(100%, 360px);
    min-height: 76px;
    padding: 0.6rem 0.65rem 0.6rem 0.6rem;
    border-radius: 1.25rem;
    background: linear-gradient(135deg, #171717, #303030);
    color: #fff;
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.2);
}

.ai-playback-card__cover {
    position: relative;
    width: 3.6rem;
    height: 3.6rem;
    flex: 0 0 3.6rem;
    overflow: hidden;
    border-radius: 0.95rem;
    background: rgba(255, 255, 255, 0.12);
}

.ai-playback-card__cover-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ai-playback-card__body {
    min-width: 0;
    flex: 1;
}

.ai-playback-card__eyebrow {
    overflow: hidden;
    color: rgba(255, 255, 255, 0.52);
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 1.2;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.ai-playback-card__title,
.ai-playback-card__artist {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.ai-playback-card__title {
    margin-top: 0.28rem;
    color: #fff;
    font-size: 0.92rem;
    font-weight: 700;
    line-height: 1.25;
}

.ai-playback-card__artist {
    margin-top: 0.2rem;
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.72rem;
    line-height: 1.2;
}

.ai-playback-card__actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-shrink: 0;
}

.ai-playback-card__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.35rem;
    height: 2.35rem;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #fff;
    color: #171717;
    cursor: pointer;
    transition: transform 160ms ease, background 160ms ease;
}

.ai-playback-card__button:hover {
    transform: translateY(-1px);
    background: #f1f5f9;
}

.ai-playback-card__button--ghost {
    width: 2rem;
    height: 2rem;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.ai-playback-card__button svg {
    width: 1.05rem;
    height: 1.05rem;
    fill: currentColor;
}

.ai-playback-card__button--ghost svg {
    width: 0.95rem;
    height: 0.95rem;
}

.ai-playback-card__equalizer {
    position: absolute;
    right: 0.4rem;
    bottom: 0.35rem;
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 0.8rem;
    padding: 0.2rem 0.25rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.58);
}

.ai-playback-card__equalizer i {
    display: block;
    width: 2px;
    height: 0.35rem;
    border-radius: 999px;
    background: #fff;
    animation: ai-playback-equalizer 760ms ease-in-out infinite alternate;
}

.ai-playback-card__equalizer i:nth-child(2) {
    height: 0.65rem;
    animation-delay: 180ms;
}

.ai-playback-card__equalizer i:nth-child(3) {
    height: 0.48rem;
    animation-delay: 320ms;
}

@keyframes ai-playback-equalizer {
    from { transform: scaleY(0.55); }
    to { transform: scaleY(1); }
}

.prose :deep(pre) {
    white-space: pre-wrap;
    word-break: break-word;
}

.prose :deep(p:last-child) {
    margin-bottom: 0;
}

@media (min-width: 768px) {
    .floating-ai-chat-launcher {
        bottom: 1.25rem;
    }

    .floating-ai-chat-sidebar {
        width: 268px;
    }

    .floating-ai-chat-panel.is-wide .floating-ai-chat-header {
        padding-top: 1rem;
        padding-bottom: 1rem;
    }

    .floating-ai-chat-header-hint {
        display: block;
    }
}

@media (min-width: 1024px) {
    .floating-ai-chat-panel.is-wide .floating-ai-chat-sidebar.is-wide {
        position: relative;
        inset: auto;
        z-index: auto;
        display: flex;
        width: 300px;
        flex-shrink: 0;
        border-right: 1px solid oklch(var(--bc) / 0.1);
        background:
            radial-gradient(circle at 16% 8%, oklch(var(--p) / 0.16), transparent 14rem),
            linear-gradient(180deg, color-mix(in oklch, oklch(var(--b2)) 82%, oklch(var(--p)) 7%), oklch(var(--b1)));
        box-shadow: inset -1px 0 0 oklch(var(--b1) / 0.6);
    }

    .floating-ai-chat-panel.is-wide .floating-ai-chat-session-backdrop {
        display: none;
    }

    .floating-ai-chat-panel.is-wide .floating-ai-chat-session-toggle {
        display: none;
    }

    .floating-ai-chat-sidebar-prompts {
        display: block;
    }
}

@media (max-width: 768px) {
    .floating-ai-chat-launcher {
        bottom: calc(76px + env(safe-area-inset-bottom));
    }

    .floating-ai-chat-launcher .floating-ai-chat-panel {
        width: calc(100vw - 1rem);
        height: calc(100vh - 6.75rem - env(safe-area-inset-bottom));
        max-height: calc(100vh - 6.75rem - env(safe-area-inset-bottom));
    }

    .floating-ai-chat-launcher .floating-ai-chat-panel.is-wide {
        width: calc(100vw - 1rem);
        height: calc(100dvh - 6.75rem - env(safe-area-inset-bottom));
        max-height: calc(100dvh - 6.75rem - env(safe-area-inset-bottom));
        border-radius: 24px;
    }
}
</style>
